import React from 'react';
import { FadeIn, Section } from './Shared';
import CloseIcon from '@mui/icons-material/Close';
export function OpeningHook() {
  return <Section className="bg-white rounded-3xl shadow-soft my-12" data-id="element-14">
      <div className="max-w-3xl mx-auto text-center" data-id="element-15">
        <FadeIn data-id="element-16">
          <p className="text-xl md:text-2xl leading-relaxed text-charcoal mb-8 font-medium" data-id="element-17">
            You're staring at the stovetop, exhausted after a long day,
            wondering if "eating healthy" has to be this complicated. You want
            to lose the weight and feel light again, but most keto plans feel
            like a second job—filled with exotic ingredients and hours of prep
            you just don't have.
          </p>
        </FadeIn>
        <FadeIn delay={0.2} data-id="element-18">
          <p className="text-lg md:text-xl leading-relaxed text-charcoal-light" data-id="element-19">
            It's time to stop overthinking and start eating. You don't need a
            degree in nutrition to see results; you just need a better blueprint
            for your kitchen.
          </p>
        </FadeIn>
      </div>
    </Section>;
}
export function Problem() {
  return <Section data-id="element-20">
      <div className="max-w-4xl mx-auto" data-id="element-21">
        <FadeIn data-id="element-22">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-23">
            The "Decision Fatigue Trap"
          </h2>
        </FadeIn>
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-24">
          <FadeIn delay={0.1} data-id="element-25">
            <div className="rounded-2xl overflow-hidden shadow-lg h-80" data-id="element-26">
              <img src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=800&q=80" alt="Frustrated woman in kitchen" className="w-full h-full object-cover" data-id="element-27" />
            </div>
          </FadeIn>
          <div className="space-y-6" data-id="element-28">
            <FadeIn delay={0.2} data-id="element-29">
              <p className="text-lg leading-relaxed text-charcoal" data-id="element-30">
                Most women over 45 fail on Keto because they fall into the
                "Decision Fatigue Trap." You try to track every macro, buy
                expensive supplements, and cook separate meals for yourself and
                your family. Eventually, it becomes too much, and you find
                yourself reaching for the bread basket just to feel full.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-31">
              <p className="text-lg leading-relaxed text-charcoal" data-id="element-32">
                "Standard" keto often relies on too much heavy dairy and
                processed junk that leaves you feeling bloated and sluggish. To
                see real changes in your "menopause middle," you need recipes
                that prioritize high-quality protein and clean fats without the
                inflammatory fluff.
              </p>
            </FadeIn>
          </div>
        </div>
      </div>
    </Section>;
}
export function WhyItDidntWork() {
  const reasons = ['You followed "dirty keto" plans that caused massive bloating and digestive backup.', 'You tried complex recipes with 20+ ingredients that left you exhausted before you even ate.', 'You relied on "low carb" processed snacks that spiked your insulin and stalled your progress.', "You weren't getting enough protein, leaving you hungry and prone to 9 PM sugar binges.", 'You lacked a simple, repeatable system that fits into a busy 45+ lifestyle.'];
  return <Section className="bg-cream-dark rounded-3xl" data-id="element-33">
      <div className="max-w-3xl mx-auto" data-id="element-34">
        <FadeIn data-id="element-35">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-36">
            Why What You Tried Didn't Work
          </h2>
        </FadeIn>
        <div className="space-y-4" data-id="element-37">
          {reasons.map((reason, index) => <FadeIn key={index} delay={0.1 * index} data-id="element-38">
              <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm" data-id="element-39">
                <div className="flex-shrink-0 mt-1 bg-terracotta/10 p-1 rounded-full text-terracotta" data-id="element-40">
                  <CloseIcon fontSize="small" data-id="element-41" />
                </div>
                <p className="text-lg text-charcoal" data-id="element-42">{reason}</p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </Section>;
}
export function UniqueMechanism() {
  return <Section data-id="element-43">
      <FadeIn data-id="element-44">
        <div className="bg-sage text-white rounded-3xl p-8 md:p-16 text-center shadow-xl relative overflow-hidden" data-id="element-45">
          {/* Background pattern */}
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white opacity-5 rounded-full blur-2xl" data-id="element-46"></div>
          <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-white opacity-5 rounded-full blur-2xl" data-id="element-47"></div>

          <div className="relative z-10 max-w-3xl mx-auto" data-id="element-48">
            <span className="inline-block py-1 px-3 rounded-full bg-white/20 text-white font-medium text-sm tracking-wide uppercase mb-6" data-id="element-49">
              The Secret
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-8 text-white" data-id="element-50">
              The High-Protein Metabolic Shortcut
            </h2>
            <p className="text-xl md:text-2xl leading-relaxed text-sage-light text-white/90" data-id="element-51">
              This isn't a complex diet "manual". By combining the fat-burning
              power of Ketosis with higher protein targets (like sirloin, wild
              salmon, and chicken), we protect your muscle mass and keep you
              full for hours.
            </p>
            <p className="text-lg md:text-xl mt-6 font-medium text-white" data-id="element-52">
              No more "keto flu," no more brain fog, and no more standing in the
              kitchen wondering what's for dinner.
            </p>
          </div>
        </div>
      </FadeIn>
    </Section>;
}